import React, { useState } from 'react';
import { ScanTarget } from '@shared/types';

interface IPRangeInputProps {
  onTargetsAdd: (targets: ScanTarget[]) => void;
  disabled?: boolean;
}

export const IPRangeInput: React.FC<IPRangeInputProps> = ({
  onTargetsAdd,
  disabled = false,
}) => {
  const [rangeInput, setRangeInput] = useState('');
  const [port, setPort] = useState<number>(443);
  const [error, setError] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);

  const parseIPRange = (range: string): string[] => {
    const trimmed = range.trim();
    
    // CIDR notation (e.g., 192.168.1.0/24)
    if (trimmed.includes('/')) {
      return parseCIDR(trimmed);
    }
    
    // Range notation (e.g., 192.168.1.1-192.168.1.10)
    if (trimmed.includes('-')) {
      return parseRange(trimmed);
    }
    
    // Single IP
    if (isValidIP(trimmed)) {
      return [trimmed];
    }
    
    throw new Error('Invalid IP range format');
  };

  const parseCIDR = (cidr: string): string[] => {
    const [network, prefixStr] = cidr.split('/');
    const prefix = parseInt(prefixStr);
    
    if (!isValidIP(network) || prefix < 0 || prefix > 32) {
      throw new Error('Invalid CIDR notation');
    }

    const networkParts = network.split('.').map(Number);
    const networkInt = (networkParts[0] << 24) + (networkParts[1] << 16) + (networkParts[2] << 8) + networkParts[3];
    const hostBits = 32 - prefix;
    const numHosts = Math.pow(2, hostBits);
    
    if (numHosts > 1000) {
      throw new Error('IP range too large (max 1000 IPs)');
    }

    const ips: string[] = [];
    for (let i = 0; i < numHosts; i++) {
      const ip = networkInt + i;
      const a = (ip >>> 24) & 0xFF;
      const b = (ip >>> 16) & 0xFF;
      const c = (ip >>> 8) & 0xFF;
      const d = ip & 0xFF;
      
      // Skip network and broadcast addresses for /24 and smaller
      if (prefix >= 24 && (i === 0 || i === numHosts - 1)) {
        continue;
      }
      
      ips.push(`${a}.${b}.${c}.${d}`);
    }
    
    return ips;
  };

  const parseRange = (range: string): string[] => {
    const [startIP, endIP] = range.split('-').map(s => s.trim());
    
    if (!isValidIP(startIP) || !isValidIP(endIP)) {
      throw new Error('Invalid IP addresses in range');
    }

    const startParts = startIP.split('.').map(Number);
    const endParts = endIP.split('.').map(Number);
    
    const startInt = (startParts[0] << 24) + (startParts[1] << 16) + (startParts[2] << 8) + startParts[3];
    const endInt = (endParts[0] << 24) + (endParts[1] << 16) + (endParts[2] << 8) + endParts[3];
    
    if (startInt > endInt) {
      throw new Error('Start IP must be less than or equal to end IP');
    }
    
    const numIPs = endInt - startInt + 1;
    if (numIPs > 1000) {
      throw new Error('IP range too large (max 1000 IPs)');
    }

    const ips: string[] = [];
    for (let i = startInt; i <= endInt; i++) {
      const a = (i >>> 24) & 0xFF;
      const b = (i >>> 16) & 0xFF;
      const c = (i >>> 8) & 0xFF;
      const d = i & 0xFF;
      ips.push(`${a}.${b}.${c}.${d}`);
    }
    
    return ips;
  };

  const isValidIP = (ip: string): boolean => {
    const parts = ip.split('.');
    if (parts.length !== 4) return false;
    
    return parts.every(part => {
      const num = parseInt(part);
      return !isNaN(num) && num >= 0 && num <= 255;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!rangeInput.trim()) {
      setError('IP range is required');
      return;
    }

    if (port < 1 || port > 65535) {
      setError('Port must be between 1 and 65535');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      const ips = parseIPRange(rangeInput);
      const targets: ScanTarget[] = ips.map(ip => ({
        type: 'ip' as const,
        value: ip,
        port,
      }));

      onTargetsAdd(targets);
      setRangeInput('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid IP range');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRangeChange = (value: string) => {
    setRangeInput(value);
    if (error) {
      setError('');
    }
  };

  return (
    <div className="ip-range-input">
      <h3>IP Range Scan</h3>
      <form onSubmit={handleSubmit} className="range-form">
        <div className="form-group">
          <label htmlFor="range">IP Range:</label>
          <input
            id="range"
            type="text"
            value={rangeInput}
            onChange={(e) => handleRangeChange(e.target.value)}
            placeholder="e.g., 192.168.1.0/24 or 192.168.1.1-192.168.1.10"
            disabled={disabled || isProcessing}
            className={error ? 'error' : ''}
          />
          {error && <span className="error-message">{error}</span>}
          <small className="help-text">
            Supported formats: CIDR (192.168.1.0/24) or Range (192.168.1.1-192.168.1.10)
            <br />
            Maximum 1000 IPs per range
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="port">Port:</label>
          <input
            id="port"
            type="number"
            value={port}
            onChange={(e) => setPort(parseInt(e.target.value) || 443)}
            min="1"
            max="65535"
            disabled={disabled || isProcessing}
          />
        </div>

        <button 
          type="submit" 
          disabled={disabled || isProcessing || !rangeInput.trim()}
          className="add-targets-btn"
        >
          {isProcessing ? 'Processing...' : 'Add IP Range'}
        </button>
      </form>
    </div>
  );
};